import { Injectable, inject } from '@angular/core';
import { Activity } from '@wilson/interfaces';
import { BehaviorSubject } from 'rxjs';
import { DragAndDropHelperService } from './drag-and-drop-helper.service';

export interface SnapInformation {
  snapPx: number;
  snapDateTime: string;
  snapRange: number[];
}
const SNAP_RANGE_ADJUSTMENT_PX = 10;
@Injectable()
export class MouseMovementHelperService {
  private readonly dragAndDropHelperService = inject(DragAndDropHelperService);

  private offsetXSubject = new BehaviorSubject<number | null>(null);
  private animationOffsetXSubject = new BehaviorSubject<number | null>(null);
  private userDraggingActivitySubject = new BehaviorSubject<boolean>(false);
  private snapPxSubject = new BehaviorSubject<number | null>(null);

  public offsetX$ = this.offsetXSubject.asObservable();
  public animationOffsetX$ = this.animationOffsetXSubject.asObservable();
  public snapPx$ = this.snapPxSubject.asObservable();
  public isUserDraggingActivity$ =
    this.userDraggingActivitySubject.asObservable();

  public setMouseOffsetX(offsetX: number | null): void {
    this.offsetXSubject.next(offsetX);
  }

  public setAnimationOffsetX(offsetX: number | null): void {
    this.animationOffsetXSubject.next(offsetX);
  }

  public setSnapPx(snapPx: number | null): void {
    this.snapPxSubject.next(snapPx);
  }

  public setUserDraggingActivity(isDragging: boolean): void {
    this.userDraggingActivitySubject.next(isDragging);
  }

  public findSnapInfo(
    offsetX: number,
    snappingInformation: SnapInformation[],
  ): SnapInformation | null {
    for (const snappingInfo of snappingInformation) {
      const [rangeStart, rangeEnd] = snappingInfo.snapRange;

      if (offsetX >= rangeStart && offsetX <= rangeEnd) {
        return snappingInfo;
      }
    }
    return null;
  }

  public generateSnappingInformation(
    activities: Activity[],
    activityId: string | null,
    snappingStepSize: number,
  ): SnapInformation[] {
    const snappingInformation: SnapInformation[] = [];
    activities.forEach((activity) => {
      if (activity.id !== activityId) {
        const startTimePx = this.dragAndDropHelperService.calculatePxFromTime({
          time: activity.startDatetime,
          snappingStepSize,
        });
        const endTimePx = this.dragAndDropHelperService.calculatePxFromTime({
          time: activity.endDatetime,
          snappingStepSize,
        });
        snappingInformation.push(
          {
            snapPx: startTimePx,
            snapDateTime: activity.startDatetime,
            snapRange: [
              startTimePx - SNAP_RANGE_ADJUSTMENT_PX,
              startTimePx + SNAP_RANGE_ADJUSTMENT_PX,
            ],
          },
          {
            snapPx: endTimePx,
            snapDateTime: activity.endDatetime,
            snapRange: [
              endTimePx - SNAP_RANGE_ADJUSTMENT_PX,
              endTimePx + SNAP_RANGE_ADJUSTMENT_PX,
            ],
          },
        );
      }
    });
    return snappingInformation;
  }
}
